import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classes from './GlobalCalendar.module.scss';

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

// Monday-start: 0=Monday, 1=Tuesday, ..., 6=Sunday.
function getFirstDayOfMonth(year, month) {
  return (new Date(year, month - 1, 1).getDay() + 6) % 7;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function dateKey(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

// Discrete heat-map bucket from a 0..1 ratio. Steps give clearer reading
// than a continuous gradient when values cluster near the extremes.
function bucket(ratio) {
  if (ratio == null) return null;
  if (ratio >= 0.95) return 5;
  if (ratio >= 0.80) return 4;
  if (ratio >= 0.50) return 3;
  if (ratio >= 0.20) return 2;
  return 1;
}

function dayCounts(globalData, dateStr) {
  const row = globalData[dateStr];
  if (!row) return null;
  const total = row.total ?? 0;
  if (total === 0) return null;
  const success = (row.processed ?? 0) + (row.duplicate ?? 0);
  return { total, success, ratio: success / total };
}

function describeCell(dateStr, counts, t) {
  if (!counts) return t('globalCellNoData', { date: dateStr });
  return t('globalCellWithCounts', {
    date: dateStr,
    success: counts.success,
    total: counts.total,
    pct: Math.round(counts.ratio * 100),
  });
}

// ─── Monthly view ─────────────────────────────────────────────────────────────

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function MonthlyView({ year, month, globalData, selectedDate, onDayClick, t }) {
  const days = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const cells = [];
  for (let i = 0; i < firstDay; i += 1) {
    cells.push(<div key={`e${i}`} className={classes.monthEmptyCell} />);
  }
  for (let d = 1; d <= days; d += 1) {
    const ds = dateKey(year, month, d);
    const counts = dayCounts(globalData, ds);
    const lvl = counts ? bucket(counts.ratio) : null;
    const description = describeCell(ds, counts, t);
    const isSelected = selectedDate === ds;
    cells.push(
      <button
        key={d}
        type="button"
        title={description}
        aria-label={description}
        className={`${classes.monthDay} ${counts ? classes[`lvl_${lvl}`] : classes.lvl_empty} ${isSelected ? classes.selected : ''}`}
        onClick={() => counts && onDayClick(ds)}
        disabled={!counts}
      >
        <span className={classes.dayNum}>{d}</span>
        {counts && (
          <span className={classes.ratio}>
            {counts.success}/{counts.total}
          </span>
        )}
      </button>,
    );
  }

  return (
    <div className={classes.monthGrid}>
      {WEEKDAYS.map((wd) => (
        <div key={wd} className={classes.monthWeekday}>{wd}</div>
      ))}
      {cells}
    </div>
  );
}

MonthlyView.propTypes = {
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  globalData: PropTypes.object.isRequired,
  selectedDate: PropTypes.string,
  onDayClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

// ─── Yearly view (compact heat-map, 12 month minis) ──────────────────────────

function YearlyMonth({ year, monthIndex, globalData, selectedDate, onDayClick, t }) {
  const monthNum = monthIndex + 1;
  const days = getDaysInMonth(year, monthNum);
  const firstDay = getFirstDayOfMonth(year, monthNum);

  const cells = [];
  for (let i = 0; i < firstDay; i += 1) {
    cells.push(<span key={`e${i}`} className={classes.yearEmptyCell} />);
  }
  for (let d = 1; d <= days; d += 1) {
    const ds = dateKey(year, monthNum, d);
    const counts = dayCounts(globalData, ds);
    const lvl = counts ? bucket(counts.ratio) : null;
    const description = describeCell(ds, counts, t);
    const isSelected = selectedDate === ds;
    cells.push(
      <button
        key={d}
        type="button"
        title={description}
        aria-label={description}
        data-tip={d}
        className={`${classes.yearDay} ${counts ? classes[`lvl_${lvl}`] : classes.lvl_empty} ${isSelected ? classes.selected : ''}`}
        onClick={() => counts && onDayClick(ds)}
        disabled={!counts}
      />,
    );
  }
  const monthName = new Date(year, monthIndex, 1).toLocaleString('en', { month: 'short' });

  return (
    <div className={classes.yearMonth}>
      <div className={classes.yearMonthLabel}>{monthName}</div>
      <div className={classes.yearDayGrid}>{cells}</div>
    </div>
  );
}

YearlyMonth.propTypes = {
  year: PropTypes.number.isRequired,
  monthIndex: PropTypes.number.isRequired,
  globalData: PropTypes.object.isRequired,
  selectedDate: PropTypes.string,
  onDayClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function YearlyView({ year, globalData, selectedDate, onDayClick, t }) {
  return (
    <div className={classes.yearGrid}>
      {Array.from({ length: 12 }, (_, i) => (
        <YearlyMonth
          key={i}
          year={year}
          monthIndex={i}
          globalData={globalData}
          selectedDate={selectedDate}
          onDayClick={onDayClick}
          t={t}
        />
      ))}
    </div>
  );
}

YearlyView.propTypes = {
  year: PropTypes.number.isRequired,
  globalData: PropTypes.object.isRequired,
  selectedDate: PropTypes.string,
  onDayClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default function GlobalCalendar({
  globalData,
  view,
  year,
  month,
  selectedDate,
  onDayClick,
}) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });

  if (view === 'month') {
    return (
      <MonthlyView
        year={year}
        month={month}
        globalData={globalData}
        selectedDate={selectedDate}
        onDayClick={onDayClick}
        t={t}
      />
    );
  }
  return (
    <YearlyView
      year={year}
      globalData={globalData}
      selectedDate={selectedDate}
      onDayClick={onDayClick}
      t={t}
    />
  );
}

GlobalCalendar.propTypes = {
  globalData: PropTypes.object.isRequired,
  view: PropTypes.oneOf(['year', 'month']).isRequired,
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  selectedDate: PropTypes.string,
  onDayClick: PropTypes.func.isRequired,
};

GlobalCalendar.defaultProps = {
  selectedDate: null,
};
